import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AuthModal from './AuthModal';
import VowAILogo from './VowAILogo';
import { API_ENDPOINTS } from '../config/api';

const Navbar = () => {
  const navigate = useNavigate();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [user, setUser] = useState(null);
  const [closeTimeout, setCloseTimeout] = useState(null);
  const location = useLocation();

  const fetchUserProfile = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await fetch(`${API_ENDPOINTS.auth}/profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.success) {
          setUser(data.user);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        localStorage.removeItem('token');
        setUser(null);
      }
    }
  };

  useEffect(() => {
    fetchUserProfile();
    
    // Close dropdown on scroll
    const handleScroll = () => {
      setActiveDropdown(null);
    };
    
    // Close dropdown on click outside
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setActiveDropdown(null);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('click', handleClickOutside);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleClickOutside);
      if (closeTimeout) {
        clearTimeout(closeTimeout);
      }
    };
  }, [closeTimeout]);

  // Listen for profile updates
  useEffect(() => {
    const handleProfileUpdate = () => {
      fetchUserProfile();
    };

    window.addEventListener('profileUpdated', handleProfileUpdate);
    return () => {
      window.removeEventListener('profileUpdated', handleProfileUpdate);
    };
  }, []);

  // Listen for auth modal open requests (from VowBot)
  useEffect(() => {
    const handleOpenAuthModal = (event) => {
      setShowAuthModal(true);
      // Could use event.detail.mode to set initial mode if needed
    };

    window.addEventListener('openAuthModal', handleOpenAuthModal);
    return () => {
      window.removeEventListener('openAuthModal', handleOpenAuthModal);
    };
  }, []);

  // Listen for localStorage changes across tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (e.newValue) {
          // Token was added/updated - fetch user profile
          fetchUserProfile();
        } else {
          // Token was removed - clear user state
          setUser(null);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId'); // Also remove userId
    setUser(null);
    setActiveDropdown(null);
    
    // Notify VowBot to clear conversation
    window.dispatchEvent(new Event('userLoggedOut'));
    
    navigate('/'); // Redirect to homepage
  };

  const getFirstName = (fullName) => {
    if (!fullName) return 'User';
    return fullName.split(' ')[0];
  };

  const menuItems = [
    {
      title: 'Wedding Blueprint',
      path: '/wedding-blueprint',
      icon: '💍',
      subItems: [
        { title: 'Dream Wedding Generator', path: '/wedding-blueprint/dream-wedding-generator' },
        { title: 'Moodboard Matcher', path: '/wedding-blueprint/moodboard-matcher' },
        { title: 'Budget Allocator', path: '/wedding-blueprint/budget-allocator' }
      ]
    },
    {
      title: 'Vendor Vault',
      path: '/vendor-vault',
      icon: '🏪',
      subItems: [
        { title: 'Smart Vendor Matching', path: '/vendor-vault/smart-vendor-matching' },
        { title: 'Vendor Shortlisting Assistant', path: '/vendor-vault/vendor-shortlisting-assistant' }
      ]
    },
    {
      title: 'WedCraft Studio',
      path: '/wedcraft-studio',
      icon: '🎨',
      subItems: [
        { title: 'Invite Generator', path: '/wedcraft-studio/invite-generator' },
        { title: 'Cultural Ritual Explainer', path: '/wedcraft-studio/cultural-ritual-explainer' }
      ]
    },
    {
      title: 'The Big Day Playbook',
      path: '/big-day-playbook',
      icon: '📋',
      subItems: [
        { title: 'Timeline Generator', path: '/big-day-playbook/timeline-generator' },
        { title: 'Weather Risk Advisor', path: '/big-day-playbook/weather-risk-advisor' }
      ]
    },
    {
      title: 'Ever After Memories',
      path: '/ever-after-memories',
      icon: '💕',
      subItems: [
        { title: 'Thank You Note Generator', path: '/ever-after-memories/thank-you-note-generator' },
        { title: 'Memory Capsule Creator', path: '/ever-after-memories/memory-capsule-creator' }
      ]
    }
  ];

  const handleDropdownToggle = (index) => {
    if (closeTimeout) {
      clearTimeout(closeTimeout);
      setCloseTimeout(null);
    }
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  const handleMouseEnter = () => {
    if (closeTimeout) {
      clearTimeout(closeTimeout);
      setCloseTimeout(null);
    }
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setActiveDropdown(null);
    }, 300); // 300ms delay
    setCloseTimeout(timeout);
  };

  const isActiveRoute = (path) => {
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="bg-white shadow-lg border-b-2 border-rose-100 sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <VowAILogo />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-1">
            {menuItems.map((item, index) => (
              <div 
                key={index} 
                className="relative group dropdown-container"
                onMouseEnter={() => {
                  handleMouseEnter();
                  setActiveDropdown(index);
                }}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  onClick={() => handleDropdownToggle(index)}
                  className={`group relative flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 ${
                    isActiveRoute(item.path)
                      ? 'bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white shadow-lg'
                      : 'text-gray-700 hover:text-rose-600'
                  }`}
                >
                  <span className="text-lg">{item.icon}</span>
                  <span>{item.title}</span>
                  <svg
                    className={`w-4 h-4 transition-transform duration-300 ${
                      activeDropdown === index ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {activeDropdown === index && (
                  <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50 animate-slideDown">
                    {item.subItems.map((subItem, subIndex) => (
                      <Link
                        key={subIndex}
                        to={subItem.path}
                        onClick={() => setActiveDropdown(null)}
                        className={`block px-5 py-3 mx-2 my-1 text-sm font-medium rounded-xl transition-all duration-200 ${
                          location.pathname === subItem.path
                            ? 'bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white'
                            : 'text-gray-700 hover:bg-rose-50 hover:text-rose-600'
                        }`}
                      >
                        {subItem.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            
            {/* Auth Button */}
            <div className="relative ml-4 dropdown-container" 
              onMouseEnter={() => {
                handleMouseEnter();
                if (user) setActiveDropdown('auth');
              }} 
              onMouseLeave={handleMouseLeave}
            >
              {user ? (
                <div className="relative">
                  <button
                    onClick={() => handleDropdownToggle('auth')}
                    className="flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-semibold text-gray-700 hover:text-rose-600 transition-all duration-300"
                  >
                    <span className="text-lg">👤</span>
                    <span>{getFirstName(user.name)}</span>
                    <svg className={`w-4 h-4 transition-transform duration-300 ${activeDropdown === 'auth' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {activeDropdown === 'auth' && (
                    <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50 animate-slideDown">
                      <Link to="/dashboard" onClick={() => setActiveDropdown(null)} className="block px-5 py-3 mx-2 my-1 text-sm font-medium text-gray-700 hover:bg-rose-50 hover:text-rose-600 rounded-xl transition-all duration-200 flex items-center">
                        <span className="mr-3 text-lg">📊</span>
                        <span>Dashboard</span>
                      </Link>
                      
                      <Link to="/profile" onClick={() => setActiveDropdown(null)} className="block px-5 py-3 mx-2 my-1 text-sm font-medium text-gray-700 hover:bg-rose-50 hover:text-rose-600 rounded-xl transition-all duration-200 flex items-center">
                        <span className="mr-3 text-lg">👤</span>
                        <span>Profile</span>
                      </Link>
                      
                      <div className="border-t border-gray-100 my-2"></div>
                      
                      <Link to="/admin/vowai-labs" onClick={() => setActiveDropdown(null)} className="block px-5 py-3 mx-2 my-1 text-sm font-medium text-gray-700 hover:bg-rose-50 hover:text-rose-600 rounded-xl transition-all duration-200 flex items-center">
                        <span className="mr-3 text-lg">🧬</span>
                        <span>VowAI Labs</span>
                      </Link>
                      
                      <Link to="/admin/evaluation" onClick={() => setActiveDropdown(null)} className="block px-5 py-3 mx-2 my-1 text-sm font-medium text-gray-700 hover:bg-rose-50 hover:text-rose-600 rounded-xl transition-all duration-200 flex items-center">
                        <span className="mr-3 text-lg">📈</span>
                        <span>Model Evaluation</span>
                      </Link>
                      
                      <Link to="/admin/ab-testing" onClick={() => setActiveDropdown(null)} className="block px-5 py-3 mx-2 my-1 text-sm font-medium text-gray-700 hover:bg-rose-50 hover:text-rose-600 rounded-xl transition-all duration-200 flex items-center">
                        <span className="mr-3 text-lg">🧪</span>
                        <span>A/B Testing</span>
                      </Link>
                      
                      <div className="border-t border-gray-100 my-2"></div>
                      
                      <button onClick={handleLogout} className="w-full text-left px-5 py-3 mx-2 my-1 text-sm font-medium text-gray-700 hover:bg-rose-50 hover:text-rose-600 rounded-xl transition-all duration-200 flex items-center">
                        <span className="mr-3 text-lg">🚪</span>
                        <span>Logout</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="group relative overflow-hidden flex items-center space-x-2 px-6 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  <span className="text-lg group-hover:animate-bounce">💖</span>
                  <span>Sign In</span>
                  {/* VowAI signature glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                </button>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg text-gray-700 hover:bg-rose-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden pb-4 border-t border-rose-100">
            {menuItems.map((item, index) => (
              <div key={index} className="mt-2">
                <button
                  onClick={() => handleDropdownToggle(index)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-left font-medium transition-colors duration-200 ${
                    isActiveRoute(item.path)
                      ? 'bg-rose-100 text-rose-700'
                      : 'text-gray-700 hover:bg-rose-50'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">{item.icon}</span>
                    <span>{item.title}</span>
                  </div>
                  <svg
                    className={`w-4 h-4 transition-transform duration-200 ${
                      activeDropdown === index ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {activeDropdown === index && (
                  <div className="mt-2 ml-4 space-y-1">
                    {item.subItems.map((subItem, subIndex) => (
                      <Link
                        key={subIndex}
                        to={subItem.path}
                        onClick={() => {
                          setActiveDropdown(null);
                          setMobileMenuOpen(false);
                        }}
                        className={`block px-4 py-2 rounded-lg text-sm transition-colors duration-200 ${
                          location.pathname === subItem.path
                            ? 'bg-rose-100 text-rose-700'
                            : 'text-gray-600 hover:bg-rose-50 hover:text-rose-600'
                        }`}
                      >
                        {subItem.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Auth Modal */}
      {showAuthModal && (
        <AuthModal 
          onClose={() => setShowAuthModal(false)}
          onSuccess={(userData) => {
            setUser(userData);
            setShowAuthModal(false);
          }}
        />
      )}
      
      {/* Simple elegant animation */}
      <style jsx>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-slideDown {
          animation: slideDown 0.2s ease-out;
        }
      `}</style>
    </nav>
  );
};

export default Navbar;
